import { useState } from "react";

type FilterBarProps = {
  regions: string[];
  timeRanges: string[];
  tagOptions: string[];
  region: string;
  timeRange: string;
  tag: string;
  sort: string;
  onRegion: (v: string) => void;
  onTimeRange: (v: string) => void;
  onTag: (v: string) => void;
  onSort: (v: string) => void;
  onReset: () => void;
};

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
        active
          ? "bg-primary-500 text-background-50 font-medium"
          : "bg-background-100 text-foreground-600 hover:bg-background-200"
      }`}
    >
      {children}
    </button>
  );
}

export default function FilterBar(props: FilterBarProps) {
  const [open, setOpen] = useState(false);
  const {
    regions,
    timeRanges,
    tagOptions,
    region,
    timeRange,
    tag,
    sort,
    onRegion,
    onTimeRange,
    onTag,
    onSort,
    onReset,
  } = props;

  const sortOptions = [
    { key: "latest", label: "按发布时间" },
    { key: "hot", label: "按热度" },
  ];

  const renderGroup = (
    label: string,
    options: string[],
    value: string,
    handler: (v: string) => void,
  ) => (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs text-foreground-400 w-14 shrink-0">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <Chip key={opt} active={value === opt} onClick={() => handler(opt)}>
            {opt}
          </Chip>
        ))}
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop / tablet inline filters */}
      <div className="hidden md:block rounded-lg bg-background-50 border border-background-200 p-4">
        <div className="flex flex-col gap-3">
          {renderGroup("地区", regions, region, onRegion)}
          {renderGroup("时间", timeRanges, timeRange, onTimeRange)}
          {renderGroup("标签", ["全部", ...tagOptions], tag, onTag)}
          <div className="flex items-center justify-between pt-3 border-t border-background-100">
            <div className="flex items-center gap-2">
              <span className="text-xs text-foreground-400 w-14 shrink-0">排序</span>
              <div className="flex gap-2">
                {sortOptions.map((opt) => (
                  <Chip key={opt.key} active={sort === opt.key} onClick={() => onSort(opt.key)}>
                    {opt.label}
                  </Chip>
                ))}
              </div>
            </div>
            <button
              type="button"
              onClick={onReset}
              className="flex items-center gap-1 text-xs text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-refresh-line" />
              重置筛选
            </button>
          </div>
        </div>
      </div>

      {/* Mobile filter bar */}
      <div className="md:hidden flex items-center gap-2">
        <select
          value={sort}
          onChange={(e) => onSort(e.target.value)}
          className="h-10 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400"
        >
          {sortOptions.map((opt) => (
            <option key={opt.key} value={opt.key}>
              {opt.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex-1 h-10 flex items-center justify-center gap-1.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium cursor-pointer"
        >
          <i className="ri-filter-3-line" />
          筛选条件
        </button>
      </div>

      {/* Mobile filter sheet */}
      <div
        className={`md:hidden fixed inset-0 z-[70] transition-opacity ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="absolute inset-0 bg-foreground-950/50" onClick={() => setOpen(false)} />
        <div
          className={`absolute inset-x-0 bottom-0 bg-background-50 rounded-t-2xl p-5 max-h-[80vh] overflow-y-auto transition-transform duration-300 ${
            open ? "translate-y-0" : "translate-y-full"
          }`}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-foreground-950">筛选与排序</h3>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="w-8 h-8 flex items-center justify-center text-xl text-foreground-500 cursor-pointer"
              aria-label="关闭"
            >
              <i className="ri-close-line" />
            </button>
          </div>
          <div className="flex flex-col gap-4">
            {renderGroup("地区", regions, region, onRegion)}
            {renderGroup("时间", timeRanges, timeRange, onTimeRange)}
            {renderGroup("标签", ["全部", ...tagOptions], tag, onTag)}
            {renderGroup(
              "排序",
              sortOptions.map((o) => o.label),
              sortOptions.find((o) => o.key === sort)?.label ?? "",
              (v) => onSort(sortOptions.find((o) => o.label === v)?.key ?? "latest"),
            )}
          </div>
          <div className="mt-6 flex gap-3">
            <button
              type="button"
              onClick={() => {
                onReset();
              }}
              className="flex-1 h-11 rounded-full border border-background-300 text-foreground-700 text-sm cursor-pointer"
            >
              重置
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="flex-1 h-11 rounded-full bg-primary-500 text-background-50 text-sm font-medium cursor-pointer"
            >
              查看结果
            </button>
          </div>
        </div>
      </div>
    </>
  );
}